import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  PhoneCall, PhoneIncoming, PhoneOutgoing, CheckCircle2, Clock, X,
  UserPlus, Loader2, Trash2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { AddCallToCrmModal } from './AddCallToCrmModal';
import { ContactNotesTasksModal } from './ContactNotesTasksModal';
import { EightXEightLinkAccountModal } from './EightXEightLinkAccountModal';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 20000;

// 8x8 brand colours (from their app icon) — lime green + dark charcoal.
// Shared across the notification tile, this modal, and the Manage Calls
// page's inline "8x8 Calls" section so the brand reads consistently.
export const EIGHT_X_EIGHT_LIME = '#CBDD2C';
export const EIGHT_X_EIGHT_DARK = '#2E2E2E';

// "Ready to submit" blue — used for a matched note's card, its "Matched to"
// tag, and the Add to CRM button's border once a row is ready, so the same
// colour tells one consistent story end-to-end.
const READY_BLUE = '#2563eb';
const READY_BLUE_BG = '#eff6ff';

// Mirrors AddCallToCrmModal's own list — kept as a plain constant here too
// so a row's inline dropdown doesn't need to fetch/depend on that modal.
const BUSINESS_UNITS = ['Structural', 'Merchant', 'Industrial'];

const enconOrange = '#ee7203';

// Splits an 8x8-saved contact name ("Tom Hare") into first/last so the "Add
// Account / Contact to CRM" modal can pre-fill both fields instead of
// leaving the rep to retype a name we already have from 8x8's own address
// book. Single-word names (e.g. just "Reception") land entirely in last
// name, since CRM's contact model requires a last name but not a first.
export const splitEightX8Name = (full) => {
  const parts = (full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: '', last: parts[0] };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
};

/** A single "Calls Made" row — matched (has an account) or unmatched.
 * Purpose/business-unit/notes now live in the parent list (`rowState`) so
 * multiple rows can be filled in independently — this row is a controlled
 * view over that shared state. "Add to CRM" here submits ONLY this row —
 * bulk submission lives in the "Add All Selected to CRM" button above the
 * list instead, so auto-populated rows are never submitted without someone
 * actively choosing to. */
function CallMadeRow({ call, purposes, row, onFieldChange, ready, submittingThis, onOpenAccount, onViewCall, onLinked, onAddOne, onDelete, onRefreshMatch }) {
  const isMatched = !!call.account_id;
  const [showLink, setShowLink] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { purpose, businessUnit, notes } = row;

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await onDelete(call.id);
    } finally {
      setDeleting(false);
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefreshMatch(call.id);
    } finally {
      setRefreshing(false);
    }
  };

  // Amber border = still needs filling in; lime green = complete/selected
  // (persists, not just a hover flash) — so a glance at the borders alone
  // shows exactly what's done and what's outstanding.
  const fieldBorderClass = 'border-2 border-[#CBDD2C] hover:border-[#CBDD2C] focus:border-[#CBDD2C] outline-none transition-colors';
  const neededBorderClass = 'border-2 border-amber-400 hover:border-[#CBDD2C] focus:border-[#CBDD2C] outline-none transition-colors';

  return (
    <div
      className="rounded-2xl border-2 p-2.5"
      style={{ borderColor: EIGHT_X_EIGHT_LIME }}
      data-testid={`8x8-call-made-row-${call.id}`}
    >
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: EIGHT_X_EIGHT_LIME }}>
          {call.direction === 'inbound'
            ? <PhoneIncoming className="w-3.5 h-3.5" style={{ color: EIGHT_X_EIGHT_DARK }} />
            : <PhoneOutgoing className="w-3.5 h-3.5" style={{ color: EIGHT_X_EIGHT_DARK }} />}
        </span>
        {isMatched ? (
          <button
            onClick={() => onOpenAccount({ id: call.account_id, name: call.account_name })}
            className="text-sm font-extrabold text-gray-900 hover:text-blue-600 hover:underline truncate text-left flex-shrink-0 max-w-[38%]"
            data-testid={`8x8-call-account-link-${call.id}`}
          >
            {call.account_name}
          </button>
        ) : call.eight_x8_name ? (
          <div className="flex items-center gap-1.5 flex-shrink-0 min-w-0 max-w-[46%]" data-testid={`8x8-call-8x8-only-name-${call.id}`}>
            <span className="text-sm font-extrabold text-gray-700 truncate" title={call.eight_x8_name}>{call.eight_x8_name}</span>
            <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600 flex-shrink-0">8x8 contact only</span>
          </div>
        ) : (
          <div className="text-sm font-extrabold text-gray-400 flex-shrink-0">Unknown number</div>
        )}
        {isMatched && (
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <select
              value={businessUnit}
              onChange={(e) => onFieldChange('businessUnit', e.target.value)}
              className={`text-xs font-semibold rounded-lg h-8 px-2 bg-white flex-shrink-0 w-32 ${businessUnit ? fieldBorderClass : neededBorderClass}`}
              data-testid={`8x8-call-business-unit-${call.id}`}
            >
              <option value="">Business unit…</option>
              {BUSINESS_UNITS.map(bu => <option key={bu} value={bu}>{bu}</option>)}
            </select>
            <select
              value={purpose}
              onChange={(e) => onFieldChange('purpose', e.target.value)}
              className={`text-xs font-semibold rounded-lg h-8 px-2 bg-white flex-shrink-0 w-32 ${fieldBorderClass}`}
              data-testid={`8x8-call-purpose-${call.id}`}
            >
              <option value="">Purpose…</option>
              {purposes.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <Checkbox
              checked={ready}
              disabled
              title={ready ? 'Ready to add to CRM' : 'A matched note (or typed notes) + Business Unit are needed to select this call'}
              className="flex-shrink-0 data-[state=checked]:bg-[#CBDD2C] data-[state=checked]:border-[#CBDD2C] data-[state=checked]:text-[#2E2E2E] border-gray-300"
              data-testid={`8x8-call-select-${call.id}`}
            />
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Remove this call"
              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0 disabled:opacity-50"
              data-testid={`8x8-call-delete-${call.id}`}
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
        {!isMatched && (
          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title="Re-check CRM for this number"
              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0 disabled:opacity-50"
              data-testid={`8x8-call-refresh-match-${call.id}`}
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Remove this call"
              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0 disabled:opacity-50"
              data-testid={`8x8-call-delete-${call.id}`}
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
      </div>
      <div className="text-xs text-gray-500 font-medium pl-10 -mt-0.5">
        {call.contact_name ? `${call.contact_name} · ` : ''}{call.phone_number}
      </div>

      {isMatched ? (
        <div className="mt-2 flex items-start gap-2">
          <textarea
            value={notes}
            onChange={(e) => onFieldChange('notes', e.target.value)}
            placeholder="What was discussed on the call?"
            rows={2}
            className={`flex-1 text-sm rounded-lg p-2.5 resize-none bg-white ${notes.trim() ? fieldBorderClass : neededBorderClass}`}
            data-testid={`8x8-call-notes-${call.id}`}
          />
          <div className="flex flex-col gap-1.5 flex-shrink-0 w-28">
            <Button
              size="sm"
              onClick={() => onViewCall({ ...call, _purpose: purpose, _businessUnit: businessUnit, _notes: notes })}
              className="rounded-full h-8 text-xs font-bold border-0 text-white"
              style={{ backgroundColor: enconOrange }}
              data-testid={`8x8-call-view-${call.id}`}
            >
              View Call
            </Button>
            <Button
              size="sm"
              onClick={() => onAddOne(call)}
              disabled={!ready || submittingThis}
              className="rounded-full h-8 text-xs font-bold disabled:opacity-100"
              style={{
                backgroundColor: ready ? READY_BLUE : '#e5e7eb',
                color: ready ? '#fff' : '#9ca3af',
                border: ready ? `2px solid ${READY_BLUE}` : '2px solid transparent',
              }}
              data-testid={`8x8-call-add-to-crm-${call.id}`}
            >
              {submittingThis ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add to CRM'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <Button
            size="sm"
            onClick={() => setShowLink(true)}
            className="w-full rounded-full h-9 text-xs font-bold text-white border-0"
            style={{ backgroundColor: EIGHT_X_EIGHT_DARK }}
            data-testid={`8x8-call-link-account-${call.id}`}
          >
            <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add Account / Contact to CRM
          </Button>
        </div>
      )}

      {showLink && (
        <EightXEightLinkAccountModal
          call={call}
          onClose={() => setShowLink(false)}
          onLinked={(result) => { setShowLink(false); onLinked?.(call.id, result); }}
        />
      )}
    </div>
  );
}

/** A single "Scheduled Calls" row — today's Zoho scheduled calls surfaced
 * alongside Calls Made/Logged. "Complete Call" opens the same completion
 * flow the purple notification bar already uses (AddCallToCrmModal in
 * scheduled mode) — on submit it logs to CRM and disappears from here. */
function ScheduledCallRow({ call, onComplete, onOpenAccount }) {
  return (
    <div className="rounded-2xl border-2 border-purple-200 bg-purple-50/40 p-2.5" data-testid={`8x8-scheduled-call-row-${call.id}`}>
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-600">
          <Clock className="w-3.5 h-3.5 text-white" />
        </span>
        <div className="flex-1 min-w-0">
          {call.account?.id ? (
            <button
              onClick={() => onOpenAccount({ id: call.account.id, name: call.account.name })}
              className="text-sm font-extrabold text-gray-900 hover:text-blue-600 hover:underline truncate text-left block"
              data-testid={`8x8-scheduled-account-link-${call.id}`}
            >
              {call.account?.name || 'Unknown account'}
            </button>
          ) : (
            <div className="text-sm font-extrabold text-gray-900 truncate">{call.account?.name || 'Unknown account'}</div>
          )}
          <div className="text-xs text-gray-500 font-medium">{call.account?.phone || call.phone || 'No number on file'}</div>
        </div>
        {call.is_overdue && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 flex-shrink-0">Overdue</span>
        )}
      </div>
      {call.description && (
        <p className="text-xs text-gray-500 italic mt-1.5 pl-10 line-clamp-2">{call.description}</p>
      )}
      <div className="mt-2 pl-10">
        <Button
          size="sm"
          onClick={() => onComplete(call)}
          className="rounded-full h-8 text-xs font-bold border-0 text-white bg-purple-600 hover:bg-purple-700"
          data-testid={`8x8-scheduled-call-complete-${call.id}`}
        >
          Complete Call
        </Button>
      </div>
    </div>
  );
}

/** Picker shown when a note's status tag is clicked — lets the rep correct a
 * wrong (or missing) match by hand. Lists this user's own recent calls
 * (last 3 days, via /8x8/calls-history) so they can pick the right one, or
 * unmatch entirely. */
function AssignNoteCallModal({ note, onClose, onAssigned }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // call.id currently being assigned, or '__unmatch__'

  useEffect(() => {
    const fmt = (d) => d.toISOString().slice(0, 10);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 3);
    api.get(`${API}/8x8/calls-history`, { params: { from_date: fmt(from), to_date: fmt(to) } })
      .then(r => setCalls(r.data?.calls || []))
      .catch(() => toast.error('Failed to load recent calls'))
      .finally(() => setLoading(false));
  }, []);

  const assign = async (callId) => {
    setBusyId(callId);
    try {
      await api.patch(`${API}/8x8/quick-notes/${note.id}/assign-call`, { call_id: callId });
      onAssigned();
    } catch {
      toast.error('Failed to assign note to that call');
    } finally {
      setBusyId(null);
    }
  };

  const unmatch = async () => {
    setBusyId('__unmatch__');
    try {
      await api.patch(`${API}/8x8/quick-notes/${note.id}/assign-call`, { call_id: null });
      onAssigned();
    } catch {
      toast.error('Failed to unmatch note');
    } finally {
      setBusyId(null);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-3" onClick={onClose} data-testid="8x8-quick-note-assign-modal">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="font-bold text-sm text-gray-900">Assign note to a call</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="8x8-quick-note-assign-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100 bg-gray-50 italic truncate">
          {note.text?.trim() ? `“${note.text}”` : '(blank note)'}
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {note.matched_call_id && (
            <button
              onClick={unmatch}
              disabled={!!busyId}
              className="w-full text-left text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg px-3 py-2 mb-1 border border-red-200"
              data-testid="8x8-quick-note-unmatch-btn"
            >
              {busyId === '__unmatch__' ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null}
              Unmatch this note
            </button>
          )}
          {loading ? (
            <p className="text-xs text-gray-400 text-center py-6">Loading recent calls…</p>
          ) : calls.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">No recent calls found in the last 3 days.</p>
          ) : calls.map((c) => (
            <button
              key={c.id}
              onClick={() => assign(c.id)}
              disabled={!!busyId}
              className={`w-full text-left rounded-lg px-3 py-2 border text-xs transition-colors ${note.matched_call_id === c.id ? 'border-blue-400 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'}`}
              data-testid={`8x8-quick-note-assign-call-${c.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-gray-800 truncate">{c.account_name || 'Unknown number'}</span>
                {busyId === c.id && <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />}
              </div>
              <div className="text-[11px] text-gray-500">
                {c.contact_name ? `${c.contact_name} · ` : ''}{c.phone_number} · {fmtTime(c.created_at)}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A single quick-note row (header "+Call" button) — free-text, autosaves as
 * the rep types. Turns blue once linked to a real 8x8 call (via
 * _link_quick_notes_to_call in call_matching.py, which runs once 8x8's CDR
 * data for that call is polled). The status tag itself is a button —
 * clicking it opens a picker to correct a wrong match or assign one by hand. */
function QuickNoteRow({ note, onTextChange, onDelete, onAssigned }) {
  const [deleting, setDeleting] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const isMatched = note.status === 'matched' && note.matched_call;

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await onDelete(note.id);
    } finally {
      setDeleting(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div
      className="rounded-2xl border-2 p-2.5"
      style={{ borderColor: isMatched ? READY_BLUE : EIGHT_X_EIGHT_LIME, backgroundColor: isMatched ? READY_BLUE_BG : 'white' }}
      data-testid={`8x8-quick-note-row-${note.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[11px] text-gray-400 font-medium">{fmtTime(note.created_at)}</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowAssign(true)}
            title="Click to correct or manually set which call this note belongs to"
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
              isMatched ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
            }`}
            data-testid={`8x8-quick-note-matched-${note.id}`}
          >
            {isMatched
              ? `Matched to ${note.matched_call.account_name || note.matched_call.phone_number || 'call'}`
              : 'Waiting to match a call…'}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            title="Delete this note"
            className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
            data-testid={`8x8-quick-note-delete-${note.id}`}
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <textarea
        value={note.text}
        onChange={(e) => onTextChange(note.id, e.target.value)}
        placeholder="Type notes about the call you're on…"
        rows={2}
        autoFocus={!!note._autoFocus}
        className="w-full text-sm rounded-lg p-2.5 resize-none bg-white border-2 border-[#CBDD2C] hover:border-[#CBDD2C] focus:border-[#CBDD2C] outline-none transition-colors"
        data-testid={`8x8-quick-note-text-${note.id}`}
      />
      {showAssign && (
        <AssignNoteCallModal
          note={note}
          onClose={() => setShowAssign(false)}
          onAssigned={() => { setShowAssign(false); onAssigned(); }}
        />
      )}
    </div>
  );
}

/**
 * Shared "Calls Made / Calls Logged" list — used both embedded inline (the
 * Manage Calls page's "8x8 Calls" section) and inside the modal below (the
 * notification bar's 8x8 tile). Fed by /8x8/active-calls (skeleton, no real
 * credentials yet, but the shape matches a real 8x8 webhook payload).
 *
 * `initialTab` / `autoCreateNote` let a caller (e.g. the header "+Call"
 * button) open this straight onto a brand-new blank Note instead of the
 * default "Calls Made" view.
 */
export function EightByEightCallsList({ callsMade, callsLogged, scheduledCalls = [], onCallLogged, onScheduledCompleted, initialTab = 'made', autoCreateNote = false, onTabChange }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const changeTab = (t) => { setActiveTab(t); onTabChange?.(t); };
  const [activeCall, setActiveCall] = useState(null);            // "View Call" → full Add Call modal
  const [accountTarget, setAccountTarget] = useState(null);       // {id, name} → Account Manager modal
  const [scheduledActiveCall, setScheduledActiveCall] = useState(null); // scheduled call being completed
  const [purposes, setPurposes] = useState([]);                  // the CRM's OUTGOING call purposes
  const [incomingPurposes, setIncomingPurposes] = useState([]);  // the CRM's INCOMING call purposes (a separate, shorter list)
  // Purpose/business-unit/notes for every "Calls Made" row, keyed by call id
  // — lifted up here (rather than living inside each row) so several calls
  // can be filled in independently before submitting.
  const [rowState, setRowState] = useState({});
  const [submittingAll, setSubmittingAll] = useState(false);      // bulk "Add All Selected" in flight
  const [submittingCallId, setSubmittingCallId] = useState(null); // single-row "Add to CRM" in flight
  const draftSaveTimers = useRef({}); // debounce timer per call.id, so we don't fire a PATCH on every keystroke

  // Quick notes (header "+Call" button) state.
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const autoCreatedRef = useRef(false);
  const noteSaveTimers = useRef({});
  const sessionCreatedNoteIdsRef = useRef([]); // ids of notes created THIS time the modal was open
  const notesRef = useRef(notes); // mirrors `notes` state so the unmount cleanup below always reads the latest text

  useEffect(() => { notesRef.current = notes; }, [notes]);

  useEffect(() => {
    api.get(`${API}/zoho/call-purposes`, { params: { direction: 'outgoing' } }).then(r => setPurposes(r.data?.purposes || [])).catch(() => {});
    api.get(`${API}/zoho/call-purposes`, { params: { direction: 'incoming' } }).then(r => setIncomingPurposes(r.data?.purposes || [])).catch(() => {});
  }, []);

  // On unmount (modal closed): first flush any pending autosave immediately
  // instead of just discarding it — a debounce timer killed mid-flight would
  // otherwise silently lose whatever was typed in the last 600ms. THEN
  // delete any note created this session that's still blank — a "+Call"
  // press or "+ New Note" that was opened and closed without ever being
  // typed into shouldn't leave an empty note sitting in the list forever.
  useEffect(() => () => {
    Object.values(draftSaveTimers.current).forEach(clearTimeout);
    Object.entries(noteSaveTimers.current).forEach(([noteId, timerId]) => {
      clearTimeout(timerId);
      const note = notesRef.current.find(n => n.id === noteId);
      if (note && note.text?.trim()) {
        api.patch(`${API}/8x8/quick-notes/${noteId}`, { text: note.text }).catch(() => {});
      }
    });
    sessionCreatedNoteIdsRef.current.forEach((noteId) => {
      const note = notesRef.current.find(n => n.id === noteId);
      if (note && !note.text?.trim()) {
        api.delete(`${API}/8x8/quick-notes/${noteId}`).catch(() => {});
      }
    });
  }, []);

  const fetchNotes = useCallback(() => {
    setNotesLoading(true);
    api.get(`${API}/8x8/quick-notes`)
      .then(r => setNotes(r.data?.notes || []))
      .catch(() => {})
      .finally(() => setNotesLoading(false));
  }, []);

  // Fetched on mount (not just when the Notes tab is opened) — the Calls Made
  // tab needs to know which calls already have a matched note, so it can
  // auto-fill that call's notes field and show it as ready to submit.
  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => {
    if (activeTab !== 'notes') return;
    fetchNotes();
  }, [activeTab, fetchNotes]);

  // Fires once, on mount, when opened via the header button — creates a
  // brand-new blank note (not a resumed draft) and focuses it. Recorded into
  // sessionCreatedNoteIdsRef so the unmount cleanup above can delete it again
  // if it's still blank when the modal closes.
  useEffect(() => {
    if (!autoCreateNote || autoCreatedRef.current) return;
    autoCreatedRef.current = true;
    api.post(`${API}/8x8/quick-notes`, { text: '' })
      .then(r => {
        const note = r.data?.note;
        if (note) {
          sessionCreatedNoteIdsRef.current.push(note.id);
          setNotes(prev => [{ ...note, _autoFocus: true }, ...prev]);
        }
      })
      .catch(() => toast.error('Failed to start a new note'));
  }, [autoCreateNote]);

  const handleNewNote = () => {
    api.post(`${API}/8x8/quick-notes`, { text: '' })
      .then(r => {
        const note = r.data?.note;
        if (note) {
          sessionCreatedNoteIdsRef.current.push(note.id);
          setNotes(prev => [{ ...note, _autoFocus: true }, ...prev]);
        }
      })
      .catch(() => toast.error('Failed to create note'));
  };

  const handleNoteTextChange = (noteId, text) => {
    setNotes(prev => prev.map(n => (n.id === noteId ? { ...n, text } : n)));
    clearTimeout(noteSaveTimers.current[noteId]);
    noteSaveTimers.current[noteId] = setTimeout(() => {
      api.patch(`${API}/8x8/quick-notes/${noteId}`, { text }).catch(() => {});
    }, 600);
  };

  const handleNoteDelete = async (noteId) => {
    try {
      await api.delete(`${API}/8x8/quick-notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch {
      toast.error('Failed to delete note');
    }
  };

  // The matched quick note (if any) for a given call — used to auto-fill
  // that call's notes field and to decide whether it's ready to submit.
  const matchedNoteForCall = useCallback(
    (call) => notes.find(n => n.status === 'matched' && n.matched_call?.id === call.id),
    [notes]
  );

  // Each row offers the CRM's own purposes for ITS direction: incoming calls the Incoming Call Purpose list, outgoing calls the Call
  // Purpose list. A saved/typed purpose that isn't on that list isn't used — the rep picks one that is.
  const purposesFor = (call) => (call.direction === 'inbound' ? incomingPurposes : purposes);
  const pickPurpose = (wanted, call) => {
    const list = purposesFor(call);
    if (wanted && list.includes(wanted)) return wanted;
    const fallback = call.direction === 'inbound' ? 'Price Enquiry' : 'Cold Call';
    return list.includes(fallback) ? fallback : '';
  };
  const getRow = (call) => {
    if (rowState[call.id]) return rowState[call.id];
    const matchedNote = matchedNoteForCall(call);
    return {
      purpose: pickPurpose(call.draft_purpose, call),
      businessUnit: call.draft_business_unit || '',
      notes: call.draft_notes || matchedNote?.text || '',
    };
  };
  const updateRowField = (call, field, value) => {
    const next = { ...getRow(call), [field]: value };
    setRowState(prev => ({ ...prev, [call.id]: next }));
    // Auto-save: whatever a rep is typing/selecting survives a closed modal,
    // a page refresh, or anything else — debounced so we're not hammering
    // the API on every keystroke while they're still typing notes.
    clearTimeout(draftSaveTimers.current[call.id]);
    draftSaveTimers.current[call.id] = setTimeout(() => {
      api.patch(`${API}/8x8/active-calls/${call.id}/draft`, {
        purpose: next.purpose,
        business_unit: next.businessUnit,
        notes: next.notes,
      }).catch(() => { /* best-effort — a failed autosave shouldn't interrupt typing */ });
    }, 600);
  };
  // Ready = a matched quick note exists for this call (auto-fills notes) OR
  // notes were typed manually — AND a Business Unit is selected. A matched
  // note is the intended path; manual typing is kept as a fallback so calls
  // with no note still work exactly as before.
  const isReady = (call) => {
    const r = getRow(call);
    const hasNote = !!matchedNoteForCall(call) || r.notes.trim().length > 0;
    return hasNote && !!r.businessUnit && !!r.purpose;
  };
  const readyCount = callsMade.filter(isReady).length;

  // A scheduled call whose number already shows up in today's Calls Made /
  // Logged means the rep has actually called them via 8x8 already — hide it
  // from Scheduled so it doesn't sit there stale (it's effectively "made").
  const normalizePhone = (p) => (p || '').replace(/\D/g, '').replace(/^0+/, '').slice(-9);
  const madePhones = new Set(
    [...callsMade, ...callsLogged].map(c => normalizePhone(c.phone_number)).filter(Boolean)
  );
  const visibleScheduled = scheduledCalls.filter(c => {
    const p = normalizePhone(c.account?.phone || c.phone);
    return !p || !madePhones.has(p);
  });

  // Shared submit logic for one call — used by both the single-row button
  // and the bulk "Add All Selected" button below. Returns true/false so the
  // callers can tally success/failure counts differently (toast wording).
  const submitCallToCrm = async (call) => {
    const { purpose, businessUnit, notes: callNotes } = getRow(call);
    const trimmedNotes = callNotes.trim();
    try {
      const res = await api.post(`${API}/zoho/create-call`, {
        account_id: call.account_id,
        contact_id: call.contact_id || null,
        call_start_time: call.created_at || null,
        call_duration_minutes: 5,
        call_type: call.direction === 'inbound' ? 'Inbound' : 'Outbound',
        call_purpose: purpose,
        subject: `${purpose} — ${call.account_name}`,
        description: trimmedNotes,
      });
      const zohoCallId = res.data?.call_id;
      if (zohoCallId) {
        api.post(`${API}/zoho/call-metadata`, {
          zoho_call_id: zohoCallId,
          business_unit: businessUnit,
          direction: call.direction === 'inbound' ? 'incoming' : 'outgoing',
          description: trimmedNotes,
        }).catch(() => {});
        await api.patch(`${API}/8x8/active-calls/${call.id}/mark-added`, { zoho_call_id: zohoCallId });
      }
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail) toast.error(detail);
      return false;
    }
  };

  // Submits exactly the one call this button lives on — no other ready rows
  // are touched, even if several are ready at once.
  const handleAddOne = async (call) => {
    if (submittingCallId || submittingAll) return;
    setSubmittingCallId(call.id);
    const ok = await submitCallToCrm(call);
    setSubmittingCallId(null);
    if (ok) toast.success('Added to CRM');
    else toast.error('Failed to add — check it still has an account/contact linked');
    onCallLogged?.();
  };

  // Submits every currently-ready row in one go — the explicit "Add All
  // Selected to CRM" action above the list, so auto-populated (matched-note)
  // rows only ever get bulk-submitted when a user deliberately clicks this,
  // never as a side-effect of clicking a single row's own button.
  const handleAddAllSelected = async () => {
    const readyCalls = callsMade.filter(isReady);
    if (readyCalls.length === 0 || submittingAll) return;
    setSubmittingAll(true);
    let ok = 0, fail = 0;
    for (const call of readyCalls) {
      const success = await submitCallToCrm(call);
      if (success) ok++; else fail++;
    }
    setSubmittingAll(false);
    if (ok) toast.success(`Added ${ok} call${ok === 1 ? '' : 's'} to CRM`);
    if (fail) toast.error(`${fail} call${fail === 1 ? '' : 's'} failed to add — check they still have an account/contact linked`);
    onCallLogged?.();
  };

  const onCompletedFromView = async (zohoCallId) => {
    if (!activeCall) return;
    try {
      await api.patch(`${API}/8x8/active-calls/${activeCall.id}/mark-added`, { zoho_call_id: zohoCallId });
    } catch { /* non-fatal */ }
    setActiveCall(null);
    onCallLogged?.();
  };

  // A row was resolved via "Add Account / Contact to CRM" — persist it onto
  // the call so the row immediately re-renders as matched.
  const handleLinked = async (callId, { account, contact }) => {
    try {
      await api.patch(`${API}/8x8/active-calls/${callId}/link`, {
        account_id: account.id,
        account_name: account.name,
        contact_id: contact?.id || null,
        contact_name: contact?.name || null,
      });
    } catch { /* non-fatal — the row just won't show as linked until next refresh */ }
    onCallLogged?.();
  };

  // Removes a call row entirely (works whether or not it has any CRM
  // account/contact matched — e.g. wrong numbers, spam, duplicates).
  const handleDelete = async (callId) => {
    try {
      await api.delete(`${API}/8x8/active-calls/${callId}`);
      toast.success('Call removed');
    } catch {
      toast.error('Failed to remove call — please try again');
    }
    onCallLogged?.();
  };

  // For an unmatched ("Unknown number") row — re-runs the Zoho phone lookup
  // in case the account/contact has since been created in CRM.
  const handleRefreshMatch = async (callId) => {
    try {
      const res = await api.post(`${API}/8x8/active-calls/${callId}/refresh-match`);
      if (res.data?.matched) {
        toast.success(`Matched to ${res.data.account_name}`);
        onCallLogged?.();
      } else {
        toast.info('Still no CRM match found for this number');
      }
    } catch {
      toast.error('Failed to refresh — please try again');
    }
  };

  // Split the "Calls Made" list into Today (top) and the rest of the week (below a
  // divider) — a call from a few days ago that's still not logged shouldn't sit
  // visually mixed in with today's, so it's clearly a leftover from earlier.
  const { callsMadeToday, callsMadeRestOfWeek } = useMemo(() => {
    const now = new Date();
    const isSameLocalDay = (iso, ref) => {
      if (!iso) return false;
      const d = new Date(iso);
      return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
    };
    const today = [];
    const restOfWeek = [];
    callsMade.forEach((c) => (isSameLocalDay(c.created_at, now) ? today : restOfWeek).push(c));
    return { callsMadeToday: today, callsMadeRestOfWeek: restOfWeek };
  }, [callsMade]);

  return (
    <>
      <div className="flex gap-1 border-b border-gray-100">
        <button
          onClick={() => changeTab('made')}
          data-testid="8x8-calls-tab-made"
          className="px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors"
          style={activeTab === 'made'
            ? { borderColor: EIGHT_X_EIGHT_LIME, color: EIGHT_X_EIGHT_DARK }
            : { borderColor: 'transparent', color: '#9ca3af' }}
        >
          Calls Made ({callsMade.length})
        </button>
        <button
          onClick={() => changeTab('logged')}
          data-testid="8x8-calls-tab-logged"
          className="px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors"
          style={activeTab === 'logged'
            ? { borderColor: EIGHT_X_EIGHT_LIME, color: EIGHT_X_EIGHT_DARK }
            : { borderColor: 'transparent', color: '#9ca3af' }}
        >
          Calls Logged in CRM ({callsLogged.length})
        </button>
        <button
          onClick={() => changeTab('notes')}
          data-testid="8x8-calls-tab-notes"
          className="px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors"
          style={activeTab === 'notes'
            ? { borderColor: EIGHT_X_EIGHT_LIME, color: EIGHT_X_EIGHT_DARK }
            : { borderColor: 'transparent', color: '#9ca3af' }}
        >
          Notes{notes.length > 0 ? ` (${notes.length})` : ''}
        </button>
        {visibleScheduled.length > 0 && (
          <button
            onClick={() => changeTab('scheduled')}
            data-testid="8x8-calls-tab-scheduled"
            className="px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors"
            style={activeTab === 'scheduled'
              ? { borderColor: '#9333ea', color: '#9333ea' }
              : { borderColor: 'transparent', color: '#9ca3af' }}
          >
            Scheduled ({visibleScheduled.length})
          </button>
        )}
      </div>

      <div className="max-h-[65vh] overflow-y-auto -mx-1 px-1 space-y-2 py-3">
        {activeTab === 'made' && readyCount > 0 && (
          <div className="flex justify-end mb-1">
            <Button
              size="sm"
              onClick={handleAddAllSelected}
              disabled={submittingAll}
              className="rounded-full h-8 text-xs font-bold border-0 text-white"
              style={{ backgroundColor: READY_BLUE }}
              data-testid="8x8-add-all-selected-to-crm"
            >
              {submittingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Add All Selected to CRM ({readyCount})
            </Button>
          </div>
        )}
        {activeTab === 'made' && visibleScheduled.length > 0 && (
          <button
            onClick={() => changeTab('scheduled')}
            data-testid="8x8-scheduled-calls-banner"
            className="w-full flex items-center gap-2.5 rounded-2xl border border-purple-200 bg-purple-50 px-3.5 py-2.5 text-left hover:bg-purple-100 transition-colors mb-1"
          >
            <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
              <Clock className="w-3.5 h-3.5 text-white" />
            </span>
            <span className="text-xs font-bold text-purple-800">
              {visibleScheduled.length} scheduled call{visibleScheduled.length === 1 ? '' : 's'} today — tap to view
            </span>
          </button>
        )}
        {activeTab === 'made' && (
          callsMade.length === 0
            ? <p className="text-xs text-gray-400 text-center py-6">No calls waiting to be logged.</p>
            : <>
              {callsMadeToday.map(c => (
                <CallMadeRow
                  key={c.id}
                  call={c}
                  purposes={purposesFor(c)}
                  row={getRow(c)}
                  onFieldChange={(field, value) => updateRowField(c, field, value)}
                  ready={isReady(c)}
                  submittingThis={submittingCallId === c.id}
                  onOpenAccount={setAccountTarget}
                  onViewCall={setActiveCall}
                  onLinked={handleLinked}
                  onAddOne={handleAddOne}
                  onDelete={handleDelete}
                  onRefreshMatch={handleRefreshMatch}
                />
              ))}
              {callsMadeRestOfWeek.length > 0 && (
                <div className="flex items-center gap-2 pt-1 pb-0.5" data-testid="8x8-calls-rest-of-week-divider">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Rest of the Week</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
              )}
              {callsMadeRestOfWeek.map(c => (
                <CallMadeRow
                  key={c.id}
                  call={c}
                  purposes={purposesFor(c)}
                  row={getRow(c)}
                  onFieldChange={(field, value) => updateRowField(c, field, value)}
                  ready={isReady(c)}
                  submittingThis={submittingCallId === c.id}
                  onOpenAccount={setAccountTarget}
                  onViewCall={setActiveCall}
                  onLinked={handleLinked}
                  onAddOne={handleAddOne}
                  onDelete={handleDelete}
                  onRefreshMatch={handleRefreshMatch}
                />
              ))}
            </>
        )}
        {activeTab === 'logged' && (
          callsLogged.length === 0
            ? <p className="text-xs text-gray-400 text-center py-6">Nothing logged yet today.</p>
            : callsLogged.map(c => (
              <div key={c.id} className="w-full flex items-center gap-3 rounded-2xl border-2 border-emerald-100 bg-emerald-50/40 px-3.5 py-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {c.account_id ? (
                    <button
                      onClick={() => setAccountTarget({ id: c.account_id, name: c.account_name })}
                      className="text-sm font-bold text-gray-800 hover:text-blue-600 hover:underline truncate text-left block"
                      data-testid={`8x8-call-logged-account-link-${c.id}`}
                    >
                      {c.account_name}
                    </button>
                  ) : (
                    <div className="text-sm font-bold text-gray-800 truncate">{c.account_name || c.phone_number}</div>
                  )}
                  <div className="text-[11px] text-gray-500">{c.contact_name || ''} · {c.phone_number}</div>
                </div>
              </div>
            ))
        )}
        {activeTab === 'notes' && (
          <>
            <button
              onClick={handleNewNote}
              className="w-full rounded-2xl border-2 border-dashed p-3 text-xs font-bold text-center hover:bg-[#CBDD2C]/10 transition-colors mb-1"
              style={{ borderColor: EIGHT_X_EIGHT_LIME, color: EIGHT_X_EIGHT_DARK }}
              data-testid="8x8-quick-note-new"
            >
              + New Note
            </button>
            {notesLoading && notes.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">Loading notes…</p>
            ) : notes.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No notes yet — tap "+ New Note" while you're on a call.</p>
            ) : notes.map(n => (
              <QuickNoteRow key={n.id} note={n} onTextChange={handleNoteTextChange} onDelete={handleNoteDelete} onAssigned={fetchNotes} />
            ))}
          </>
        )}
        {activeTab === 'scheduled' && (
          visibleScheduled.length === 0
            ? <p className="text-xs text-gray-400 text-center py-6">No scheduled calls for today.</p>
            : visibleScheduled.map(c => (
              <ScheduledCallRow key={c.id} call={c} onComplete={setScheduledActiveCall} onOpenAccount={setAccountTarget} />
            ))
        )}
      </div>

      {scheduledActiveCall && (
        <AddCallToCrmModal
          isOpen={!!scheduledActiveCall}
          onClose={() => setScheduledActiveCall(null)}
          scheduledCall={scheduledActiveCall}
          onCompleted={() => { setScheduledActiveCall(null); onScheduledCompleted?.(); }}
          onDeleted={() => { setScheduledActiveCall(null); onScheduledCompleted?.(); }}
        />
      )}

      {activeCall && (
        <AddCallToCrmModal
          isOpen={!!activeCall}
          onClose={() => setActiveCall(null)}
          onCompleted={onCompletedFromView}
          prefillAccount={{
            account: activeCall.account_id ? { id: activeCall.account_id, name: activeCall.account_name } : null,
            contact: activeCall.contact_id ? { id: activeCall.contact_id } : null,
            call_purpose: activeCall._purpose,
            business_unit: activeCall._businessUnit,
            direction: activeCall.direction === 'inbound' ? 'incoming' : 'outgoing',
            description: activeCall._notes,
          }}
        />
      )}

      {accountTarget && (
        <ContactNotesTasksModal
          isOpen={!!accountTarget}
          onClose={() => setAccountTarget(null)}
          initialAccount={accountTarget}
        />
      )}
    </>
  );
}

/**
 * Notification-bar version — polls its own data, sized/positioned the same
 * way as AddCallToCrmModal (tall, centred, near-full-height) with the 8x8
 * lime border. A plain portal (not a Radix Dialog) so nothing nested inside
 * it — Add Call, the Account Manager modal, the Link Account modal — ever
 * fights it for focus.
 *
 * `initialTab` / `autoCreateNote` are forwarded straight to
 * EightByEightCallsList — used by the header "+Call" button to open
 * straight onto a fresh blank Note.
 */
export const EightXEightCallsModal = ({ open, isOpen, onClose, initialTab = 'made', autoCreateNote = false, resumeSnapshot = null }) => {
  const effectiveOpen = open ?? isOpen;
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  const effectiveInitialTab = resumeSnapshot?.activeTab || initialTab;
  const [callsMade, setCallsMade] = useState([]);
  const [callsLogged, setCallsLogged] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [polling, setPolling] = useState(false);

  // Modal UI v2 — register as a resumable "In Progress" activity the moment
  // it's opened (its own fields — quick notes, per-row purpose/business
  // unit/notes drafts — already autosave server-side, so reopening just
  // re-fetches; only the active tab needs remembering).
  useEffect(() => {
    if (!effectiveOpen || !modalUiV2 || activityIdRef.current) return;
    activityIdRef.current = resumeSnapshot?.activityId || 'main';
    activityBar.openActivity({
      type: 'eight_by_eight_calls',
      activityId: activityIdRef.current,
      title: '8x8 Calls',
      subtitle: 'Calls made/received vs. logged in CRM',
      snapshot: { activityId: activityIdRef.current, activeTab: effectiveInitialTab },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen, modalUiV2]);

  const handleTabChange = (tab) => {
    if (!modalUiV2 || !activityIdRef.current) return;
    activityBar.updateActiveSnapshot(
      { activityId: activityIdRef.current, activeTab: tab },
      { title: '8x8 Calls', subtitle: 'Calls made/received vs. logged in CRM' }
    );
  };

  const handleClose = () => {
    if (modalUiV2) activityBar.discardActive();
    onClose();
  };

  // Modal UI v2 keeps the header interactive above the overlay — lock page scroll.
  useEffect(() => {
    if (!effectiveOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [effectiveOpen, modalUiV2]);

  const fetchCalls = useCallback(async () => {
    try {
      const res = await api.get(`${API}/8x8/active-calls`);
      setCallsMade(res.data.calls_made || []);
      setCallsLogged(res.data.calls_logged || []);
    } catch { /* silent — this is a best-effort skeleton feature */ }
  }, []);

  const fetchScheduled = useCallback(async () => {
    try {
      const res = await api.get(`${API}/zoho/my-scheduled-calls`);
      setScheduledCalls(res.data?.calls || []);
    } catch { /* silent — Zoho session may not be linked */ }
  }, []);

  // Forces a fresh 8x8 Work Analytics CDR pull right now (rather than
  // waiting for the 90s background poller), then re-fetches this modal's
  // list so anything new lands immediately.
  const handlePollNow = async () => {
    if (polling) return;
    setPolling(true);
    try {
      const res = await api.post(`${API}/8x8/poll-now`);
      const { configured, fetched, staged } = res.data || {};
      if (!configured) {
        toast.info('8x8 Work Analytics isn\u2019t connected yet — add the API Key/Secret in Settings > Integrations > 8x8');
      } else if (staged > 0) {
        toast.success(`Pulled ${fetched} call${fetched === 1 ? '' : 's'} — ${staged} new`);
      } else {
        toast.info('No new calls since the last check');
      }
      await fetchCalls();
    } catch {
      toast.error('Failed to refresh — please try again');
    } finally {
      setPolling(false);
    }
  };

  useEffect(() => {
    if (!effectiveOpen) return;
    fetchCalls();
    fetchScheduled();
    const t = setInterval(() => { fetchCalls(); fetchScheduled(); }, POLL_MS);
    return () => clearInterval(t);
  }, [effectiveOpen, fetchCalls, fetchScheduled]);

  // Also refresh Scheduled the instant a call is completed/rescheduled/deleted
  // anywhere else in the app (e.g. the purple notification bar up top).
  useEffect(() => {
    if (!effectiveOpen) return;
    const handler = () => { fetchScheduled(); fetchCalls(); };
    window.addEventListener('c30:calls-changed', handler);
    return () => window.removeEventListener('c30:calls-changed', handler);
  }, [effectiveOpen, fetchScheduled, fetchCalls]);

  // Instant push while the modal is open — as soon as the 8x8 poller stages
  // a new call for this user, refresh right away instead of waiting for
  // the next POLL_MS tick.
  useEffect(() => {
    if (!effectiveOpen) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let es; let retryTimer;
    const connect = () => {
      es = new EventSource(`${API}/calls/sse?token=${encodeURIComponent(token)}`);
      es.addEventListener('8x8_calls_changed', fetchCalls);
      es.onerror = () => { es.close(); retryTimer = setTimeout(connect, 5000); };
    };
    connect();
    return () => { es?.close(); clearTimeout(retryTimer); };
  }, [effectiveOpen, fetchCalls]);

  if (!effectiveOpen) return null;

  return createPortal(
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-3 ${modalV2OverlayClass(modalUiV2)}`}
      style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 80 }}
      data-testid="8x8-calls-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden"
        style={{ border: `3px solid ${EIGHT_X_EIGHT_LIME}`, maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 24px)` : '95vh' }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: EIGHT_X_EIGHT_LIME }}>
              <PhoneCall className="w-4.5 h-4.5" style={{ color: EIGHT_X_EIGHT_DARK }} />
            </span>
            <div>
              <h3 className="text-base font-extrabold text-gray-900">Calls</h3>
              <p className="text-xs text-gray-500">Calls made/received via 8x8 vs. what's already logged in CRM.</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={handlePollNow}
              disabled={polling}
              title="Poll 8x8 for the most recent calls right now"
              className="flex items-center gap-1.5 rounded-full h-9 px-3.5 text-xs font-bold text-white border-0 disabled:opacity-70"
              style={{ backgroundColor: EIGHT_X_EIGHT_DARK }}
              data-testid="8x8-calls-modal-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${polling ? 'animate-spin' : ''}`} />
              {polling ? 'Polling…' : 'Refresh'}
            </button>
            <button
              onClick={handleClose}
              className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400"
              data-testid="8x8-calls-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-1">
          <EightByEightCallsList
            callsMade={callsMade}
            callsLogged={callsLogged}
            scheduledCalls={scheduledCalls}
            onCallLogged={fetchCalls}
            onScheduledCompleted={() => {
              fetchScheduled();
              fetchCalls();
              window.dispatchEvent(new Event('c30:calls-changed'));
            }}
            initialTab={effectiveInitialTab}
            autoCreateNote={autoCreateNote}
            onTabChange={handleTabChange}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EightXEightCallsModal;
